import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { FiHeadphones } from 'react-icons/fi';
import * as chatwoot from '../../services/chatwoot';
import { Button } from '../ui';

/**
 * Opens the support chat straight from the header.
 *
 * Support started out as an entry inside the help drawer, which meant three
 * taps to reach a human: help → read → "Talk to support". The owner asked for
 * one. The drawer entry stays for the "the manual did not answer it" path;
 * this is the direct one.
 *
 * NOT a floating bubble — that is bottom-right, which on a phone is where the
 * job-card action bar and the FABs live. Chatwoot's own bubble is hidden
 * (hideMessageBubble in services/chatwoot.js) for exactly that reason.
 */
export default function SupportButton({ showLabel = false }) {
  const { t } = useTranslation();

  // Two flags, deliberately. `configured` decides whether this button EXISTS,
  // `ready` only whether it can open right now — hiding it on !ready made a
  // Chatwoot outage look like the feature had been removed. See onStateChange.
  const [support, setSupport] = useState(() => ({
    configured: chatwoot.isConfigured(),
    ready: chatwoot.isReady(),
  }));

  useEffect(() => chatwoot.onStateChange(() => setSupport({
    configured: chatwoot.isConfigured(),
    ready: chatwoot.isReady(),
  })), []);

  if (!support.configured) return null;

  const button = (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => {
        if (!chatwoot.open()) toast.error(t('manual.supportUnavailable'));
      }}
      aria-label={t('manual.contactSupport')}
      title={t('manual.contactSupport')}
      data-testid="header-contact-support"
    >
      <FiHeadphones size={18} />
    </Button>
  );

  // The account menu wants a labelled row, and it has to disappear WITH the
  // button — hence drawn here rather than by the caller, which does not
  // subscribe to whether support exists.
  if (!showLabel) return button;

  return (
    <div className="flex items-center justify-between px-3.5 py-2">
      <span className="text-sm font-medium text-content-secondary">{t('manual.contactSupport')}</span>
      {button}
    </div>
  );
}
